import { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'
import { RoomData, RoomFilters, RoomSearchParams } from '../config/settings'
import { formatApiError } from '../utils/formatApiError'

type RoomsPagination = {
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number
  to: number
}

type RoomsApiPayload = {
  data?: RoomData[]
  pagination?: RoomsPagination
} & Partial<RoomsPagination>

interface UseRoomsReturn {
  rooms: RoomData[]
  loading: boolean
  error: string | null
  pagination: {
    current_page: number
    last_page: number
    per_page: number
    total: number
    from: number
    to: number
  } | null
  fetchRooms: (filters?: RoomFilters, append?: boolean) => Promise<void>
  searchRooms: (searchParams: RoomSearchParams) => Promise<void>
  refetch: () => Promise<void>
}

export const useRooms = (initialFilters?: RoomFilters): UseRoomsReturn => {
  const [rooms, setRooms] = useState<RoomData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<RoomsPagination | null>(null)
  const [currentFilters, setCurrentFilters] = useState<RoomFilters | undefined>(initialFilters)

  const fetchRooms = useCallback(async (filters?: RoomFilters, append: boolean = false) => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiService.getRooms(filters || currentFilters)

      if (response.success && response.data) {
        const payload = response.data as unknown as RoomsApiPayload
        const paginationData = payload.pagination ?? payload
        const roomsData = payload.data ?? []

        if (append && filters?.page && filters.page > 1) {
          setRooms((prev) => {
            const existingIds = new Set(prev.map((r) => r.id))
            const newRooms = (roomsData as RoomData[]).filter((r) => !existingIds.has(r.id))
            return newRooms.length > 0 ? [...prev, ...newRooms] : prev
          })
        } else {
          setRooms(roomsData as RoomData[])
        }
        setPagination({
          current_page: Number(paginationData?.current_page) || 1,
          last_page: Number(paginationData?.last_page) || 1,
          per_page: Number(paginationData?.per_page) || 10,
          total: Number(paginationData?.total) || 0,
          from: Number(paginationData?.from) || 0,
          to: Number(paginationData?.to) || 0,
        })
        setCurrentFilters(filters || currentFilters)
      } else {
        setError(formatApiError(response.message || 'Failed to fetch rooms'))
        if (!append) {
          setRooms([])
        }
        setPagination(null)
      }
    } catch (err) {
      setError(formatApiError(err instanceof Error ? err.message : 'An unknown error occurred'))
      if (!append) {
        setRooms([])
      }
      setPagination(null)
    } finally {
      setLoading(false)
    }
  }, [currentFilters])

  const searchRooms = useCallback(async (searchParams: RoomSearchParams) => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiService.searchRooms(searchParams)

      if (response.success && response.data) {
        setRooms(response.data.data)
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          per_page: response.data.per_page,
          total: response.data.total,
          from: response.data.from,
          to: response.data.to,
        })
        const updatedFilters: RoomFilters = {
          checkin: searchParams.checkin,
          checkout: searchParams.checkout,
          beds: searchParams.beds,
          baths: searchParams.baths,
          guests: searchParams.guests,
          sort_by: searchParams.sort_by,
          rents:
            searchParams.price_min && searchParams.price_max
              ? `${searchParams.price_min}-${searchParams.price_max}`
              : undefined,
          ammenities: searchParams.ammenities,
        }
        setCurrentFilters(updatedFilters)
      } else {
        setError(formatApiError(response.message || 'Failed to search rooms'))
        setRooms([])
        setPagination(null)
      }
    } catch (err) {
      setError(formatApiError(err instanceof Error ? err.message : 'An unknown error occurred'))
      setRooms([])
      setPagination(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const refetch = useCallback(async () => {
    await fetchRooms(currentFilters)
  }, [fetchRooms, currentFilters])

  useEffect(() => {
    if (initialFilters) {
      fetchRooms(initialFilters)
    }
  }, [])

  return {
    rooms,
    loading,
    error,
    pagination,
    fetchRooms,
    searchRooms,
    refetch,
  }
}

export default useRooms
